import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import WhatsAppButton from 'components/ui/WhatsAppButton';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const CertificationsPage = () => {
  const certifications = [
    {
      id: 1,
      title: 'Certification CRF',
      description: 'Certification reconnue en formation professionnelle et développement des compétences, garantissant l\'excellence dans la gestion et l\'animation de programmes de formation.',
      icon: 'Award',
      features: [
        'Formation certifiante reconnue',
        'Expertise en ingénierie pédagogique',
        'Accompagnement personnalisé',
        'Validation des compétences'
      ]
    },
    {
      id: 2,
      title: 'Certification ESCEN',
      description: 'École Supérieure de Commerce & de l\'Économie Numérique - Certification d\'excellence en commerce digital et stratégies numériques innovantes.',
      icon: 'GraduationCap',
      features: [
        'Commerce digital avancé',
        'Stratégies numériques',
        'Innovation et transformation',
        'Expertise reconnue internationalement'
      ]
    },
    {
      id: 3,
      title: 'Certification PMP',
      description: 'Project Management Professional - Certification internationale de référence en gestion de projet, reconnue mondialement pour l\'excellence en management de projets.',
      icon: 'Briefcase',
      features: [
        'Gestion de projet professionnelle',
        'Standards internationaux PMI',
        'Leadership et méthodologies',
        'Reconnaissance mondiale'
      ]
    },
    {
      id: 4,
      title: 'Certification Google Marketing Digital',
      description: 'Certification officielle Google en marketing digital, couvrant les stratégies publicitaires en ligne, l\'analyse de données et l\'optimisation des campagnes digitales.',
      icon: 'TrendingUp',
      features: [
        'Marketing digital Google',
        'Publicité en ligne',
        'Analytics et optimisation',
        'Certification officielle Google'
      ]
    }
  ];

  const benefits = [
    {
      icon: 'Award',
      title: 'Certifications Reconnues',
      description: 'Nos certifications sont reconnues au niveau international et valorisent votre profil professionnel.'
    },
    {
      icon: 'Users',
      title: 'Experts Qualifiés',
      description: 'Bénéficiez de l\'expertise de formateurs qualifiés et d\'experts reconnus dans leur domaine.'
    },
    {
      icon: 'TrendingUp',
      title: 'Développement Continu',
      description: 'Profitez d\'un accompagnement personnalisé et d\'un suivi régulier pour garantir votre progression.'
    },
    {
      icon: 'Target',
      title: 'Approche Personnalisée',
      description: 'Chaque parcours de certification est adapté à vos objectifs professionnels et votre contexte.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Certifications Professionnelles | CRF PERFECTION</title>
        <meta 
          name="description" 
          content="Découvrez nos certifications professionnelles reconnues: CRF, ESCEN, PMP et Google Marketing Digital pour développer votre excellence professionnelle." 
        />
        <meta 
          name="keywords" 
          content="certification CRF, certification ESCEN, certification PMP, Google Marketing Digital, formation professionnelle, CRF PERFECTION" 
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />
        <WhatsAppButton />

        {/* Section 1: Hero */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(rgba(155, 142, 87, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(155, 142, 87, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }} />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Icon name="Award" size={20} style={{ color: '#9B8E57' }} />
                <span className="text-sm font-semibold" style={{ color: '#9B8E57' }}>
                  Excellence en Certification Professionnelle
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Nos Certifications Professionnelles
              </h1>
              
              <p className="text-xl md:text-2xl font-semibold mb-6" style={{ color: '#9B8E57' }}>
                Développez vos compétences avec nos certifications reconnues
              </p>
              
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                Accédez à nos certifications professionnelles et bénéficiez d'un accompagnement 
                personnalisé pour atteindre vos objectifs de développement de carrière.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                  style={{ 
                    backgroundColor: '#96121D',
                    boxShadow: '0 4px 14px rgba(150, 18, 29, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#7d0f18';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#96121D';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  Contactez-nous
                  <Icon name="ArrowRight" size={20} className="ml-2" />
                </Link>
                
                <Link 
                  to="/professional-training-services"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg border-2 transition-all duration-300"
                  style={{ 
                    borderColor: '#9B8E57',
                    color: '#9B8E57'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#9B8E57';
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#9B8E57';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  Nos Formations
                  <Icon name="GraduationCap" size={20} className="ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Certifications Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nos Certifications
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Quatre certifications reconnues pour développer votre excellence professionnelle
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {certifications?.map((cert, index) => (
                <div
                  key={cert?.id}
                  className="group bg-white rounded-2xl p-8 transition-all duration-300 hover:shadow-2xl border-2 border-gray-100"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = index % 2 === 0 ? '#96121D' : '#9B8E57';
                    e.currentTarget.style.transform = 'translateY(-8px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#f3f4f6';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Icon */}
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-6 transition-all duration-300"
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)'
                    }}
                  >
                    <Icon 
                      name={cert?.icon} 
                      size={32} 
                      style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                    />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {cert?.title}
                  </h3>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {cert?.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-3">
                    {cert?.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Icon 
                          name="CheckCircle2" 
                          size={20} 
                          style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                          className="mr-2 flex-shrink-0 mt-0.5" 
                        />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 3: Benefits */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Pourquoi Choisir Nos Certifications?
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Nos certifications vous offrent un avantage compétitif reconnu internationalement 
                  et un accompagnement personnalisé tout au long de votre parcours professionnel.
                </p>

                <div className="space-y-6">
                  {benefits?.map((benefit, index) => (
                    <div key={index} className="flex items-start">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mr-4"
                        style={{ 
                          backgroundColor: index % 2 === 0 ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)'
                        }}
                      >
                        <Icon 
                          name={benefit?.icon} 
                          size={24} 
                          style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2 text-lg">
                          {benefit?.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {benefit?.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Content - Image */}
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <Image
                    src="/assets/images/team_collaboration_presentation_one.png"
                    alt="Professional team collaboration during certification training session showing experts presenting to engaged participants in modern conference room with CRF PERFECTION branding"
                    className="w-full h-auto"
                  />
                  {/* Overlay Badge */}
                  <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl px-6 py-4 shadow-lg">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(150, 18, 29, 0.1)' }}
                      >
                        <Icon name="Award" size={24} style={{ color: '#96121D' }} />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900">150+</div>
                        <div className="text-sm text-gray-600">Professionnels Certifiés</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: CTA */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(rgba(155, 142, 87, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(155, 142, 87, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }} />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Icon name="Sparkles" size={20} style={{ color: '#9B8E57' }} />
              <span className="text-sm font-semibold" style={{ color: '#9B8E57' }}>
                Commencez Votre Parcours
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à Développer Vos Compétences?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Commencez dès aujourd'hui votre parcours de certification avec CRF PERFECTION et 
              bénéficiez de l'expertise de nos formateurs reconnus internationalement.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                style={{ 
                  backgroundColor: '#96121D',
                  boxShadow: '0 4px 14px rgba(150, 18, 29, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#7d0f18';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#96121D';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Contactez-nous
                <Icon name="ArrowRight" size={20} className="ml-2" />
              </Link>

              <Link
                to="/service-request-form"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg border-2 transition-all duration-300"
                style={{ 
                  borderColor: '#9B8E57',
                  color: '#9B8E57',
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#9B8E57';
                  e.target.style.color = '#ffffff';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#9B8E57';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Demander un Devis
                <Icon name="FileText" size={20} className="ml-2" />
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CertificationsPage;